// REQ-40 (Faz-7) — servis API anahtarı yönetim ekranı (security.manage).
//
// Düz metin anahtar yalnızca üretim/rotate sonrası bir kez modal'da gösterilir;
// bileşen onu geçici state'te tutar ve modal kapanınca null'a çeker (unutulur).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { KeyRound, Plus, RefreshCw, ShieldAlert, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader';
import { ROUTES } from '@shared/constants/routes';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { apiKeysApi } from '@api/apiKeys';
import type { ApiKey, GeneratedApiKey } from '../types';
import { GeneratedKeyModal } from '../components/GeneratedKeyModal';

const { Text } = Typography;

function CreateKeyModal({
  open,
  saving,
  onCancel,
  onCreate,
}: {
  open: boolean;
  saving: boolean;
  onCancel: () => void;
  onCreate: (name: string) => void;
}) {
  const { t } = useTranslation(['api-keys', 'common']);
  const [form] = Form.useForm<{ name: string }>();

  return (
    <Modal
      open={open}
      title={t('create.title')}
      okText={t('create.submit')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={saving}
      onCancel={onCancel}
      afterClose={() => form.resetFields()}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={(v) => onCreate(v.name.trim())}>
        <Form.Item
          name="name"
          label={t('create.nameLabel')}
          rules={[{ required: true, message: t('create.nameRequired') }]}
        >
          <Input placeholder={t('create.namePlaceholder')} maxLength={120} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function ApiKeysPage() {
  const { t } = useTranslation(['api-keys', 'common']);
  const canManage = useCan(PERMISSIONS.SECURITY_MANAGE);
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  // Düz metin anahtar — yalnız modal ömrü boyunca; kapanınca null.
  const [generated, setGenerated] = useState<GeneratedApiKey | null>(null);

  const { data: keys = [], isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiKeysApi.list(),
    enabled: canManage,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['api-keys'] });

  const generateMutation = useMutation({
    mutationFn: (name: string) => apiKeysApi.generate(name),
    onSuccess: async (result) => {
      setCreateOpen(false);
      setGenerated(result);
      toast.success(t('toast.generated'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.rotate(id),
    onSuccess: async (result) => {
      setGenerated(result);
      toast.success(t('toast.rotated'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: async () => {
      toast.success(t('toast.revoked'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const breadcrumb = [{ label: t('common:labels.home'), to: ROUTES.DASHBOARD }, { label: t('title') }];

  if (!canManage) {
    return (
      <div>
        <PageHeader title={t('title')} breadcrumb={breadcrumb} />
        <Card>
          <Alert
            type="error"
            showIcon
            icon={<ShieldAlert size={18} />}
            message={t('noAccess.title')}
            description={t('noAccess.description')}
          />
        </Card>
      </div>
    );
  }

  const columns: ColumnsType<ApiKey> = [
    { title: t('columns.name'), dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
    {
      title: t('columns.prefix'),
      dataIndex: 'prefix',
      key: 'prefix',
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: t('columns.status'),
      key: 'status',
      render: (_, r) =>
        r.isActive ? (
          <Tag color="green">{t('status.active')}</Tag>
        ) : (
          <Tag color="red">{t('status.revoked')}</Tag>
        ),
    },
    {
      title: t('columns.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('columns.lastUsedAt'),
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (v: string | null) =>
        v ? (
          dayjs(v).format('DD.MM.YYYY HH:mm')
        ) : (
          <Text type="secondary">{t('never')}</Text>
        ),
    },
    {
      title: t('columns.actions'),
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Popconfirm
            title={t('rotate.confirmTitle')}
            description={t('rotate.confirmDescription', { name: r.name })}
            okText={t('rotate.ok')}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => rotateMutation.mutate(r.id)}
            disabled={!r.isActive}
          >
            <Button
              type="text"
              size="small"
              icon={<RefreshCw size={15} />}
              disabled={!r.isActive}
              loading={rotateMutation.isPending && rotateMutation.variables === r.id}
              aria-label={t('rotate.button')}
            />
          </Popconfirm>
          <Popconfirm
            title={t('revoke.confirmTitle')}
            description={t('revoke.confirmDescription', { name: r.name })}
            okText={t('revoke.ok')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => revokeMutation.mutate(r.id)}
            disabled={!r.isActive}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={15} />}
              disabled={!r.isActive}
              loading={revokeMutation.isPending && revokeMutation.variables === r.id}
              aria-label={t('revoke.button')}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div data-testid="api-keys-page">
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={breadcrumb}
        actions={
          <Button type="primary" icon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
            {t('new')}
          </Button>
        }
      />

      <Card>
        <Table<ApiKey>
          rowKey="id"
          columns={columns}
          dataSource={keys}
          loading={isLoading}
          pagination={false}
          locale={{
            emptyText: (
              <Space direction="vertical" align="center" style={{ padding: '24px 0' }}>
                <KeyRound size={28} />
                <Text type="secondary">{t('empty')}</Text>
              </Space>
            ),
          }}
        />
      </Card>

      <CreateKeyModal
        open={createOpen}
        saving={generateMutation.isPending}
        onCancel={() => setCreateOpen(false)}
        onCreate={(name) => generateMutation.mutate(name)}
      />

      <GeneratedKeyModal generated={generated} onClose={() => setGenerated(null)} />
    </div>
  );
}
